import { useEffect, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { LockKeyhole, LogOut, RefreshCw, Search, ShieldCheck, BookOpenCheck, Package } from "lucide-react";
import { getAdminMe, adminLogout, startAdminLogin, fetchAdminOrders } from "@/lib/adminAuth";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";

function formatAmount(amount, currency) {
  if (amount == null) return "—";
  const symbol = currency === "INR" ? "₹" : `${currency} `;
  return `${symbol}${(amount / 100).toFixed(0)}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

/** Owner-only Order History — Emergent-managed Google Auth, restricted
 * server-side to ADMIN_ALLOWED_EMAIL. Look up any past purchase by
 * (partial) Razorpay payment ID, or browse the most recent purchases. */
export default function AdminOrders() {
  const location = useLocation();
  const [authChecking, setAuthChecking] = useState(location.state?.user ? false : true);
  const [user, setUser] = useState(location.state?.user || null);
  const [authError, setAuthError] = useState(location.state?.authError || "");

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      const me = await getAdminMe();
      setUser(me);
      setAuthChecking(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOrders = useCallback(async (paymentId) => {
    setOrdersLoading(true);
    setOrdersError("");
    try {
      const data = await fetchAdminOrders(paymentId);
      setOrders(data.orders || []);
    } catch (err) {
      setOrdersError(err.message);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) loadOrders();
  }, [user, loadOrders]);

  const handleLogout = async () => {
    await adminLogout();
    setUser(null);
  };

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1B2A]">
        <p className="text-white/50 text-sm tracking-[0.14em] uppercase">Checking access…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login-gate" className="min-h-screen flex items-center justify-center bg-[#0D1B2A] px-6">
        <div className="max-w-sm w-full text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-6">
            <LockKeyhole size={22} className="text-[#F57F17]" />
          </div>
          <h1 className="text-white font-serif text-2xl mb-2">LifeIQ Admin</h1>
          <p className="text-white/50 text-sm mb-8">Sign in with the owner Google account to view order history.</p>
          {authError && (
            <p data-testid="admin-login-error" className="text-red-400 text-sm mb-6">
              {authError}
            </p>
          )}
          <button
            type="button"
            data-testid="admin-google-login-btn"
            onClick={startAdminLogin}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[13px] font-semibold tracking-wide uppercase rounded-sm bg-[#F57F17] text-white hover:bg-[#D86B0F] transition-colors"
          >
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-orders-page" className="min-h-screen bg-[#0D1B2A] px-6 py-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#F57F17] font-semibold mb-1">LifeIQ Admin</p>
            <h1 className="text-white font-serif text-2xl">Order History</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/admin/physical-orders"
              data-testid="admin-nav-physical-orders-link"
              className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[12px] uppercase tracking-wide transition-colors"
            >
              <Package size={14} /> Printed Orders
            </Link>
            <Link
              to="/admin/book-interest"
              data-testid="admin-nav-print-interest-link"
              className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[12px] uppercase tracking-wide transition-colors"
            >
              <BookOpenCheck size={14} /> Print Interest
            </Link>
            <span data-testid="admin-user-email" className="text-white/50 text-sm">{user.email}</span>
            <button
              type="button"
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[12px] uppercase tracking-wide transition-colors"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>

        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              data-testid="admin-order-search-input"
              placeholder="Search by Razorpay payment ID (e.g. pay_XXXXXXXX)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadOrders(query.trim())}
              className="pl-9 bg-white/5 border-white/15 text-white placeholder:text-white/35"
            />
          </div>
          <button
            type="button"
            data-testid="admin-order-search-btn"
            onClick={() => loadOrders(query.trim())}
            className="px-5 py-2 text-[12px] font-semibold uppercase tracking-wide rounded-sm bg-[#F57F17] text-white hover:bg-[#D86B0F] transition-colors"
          >
            Search
          </button>
          <button
            type="button"
            data-testid="admin-order-refresh-btn"
            onClick={() => { setQuery(""); loadOrders(); }}
            title="Show recent orders"
            className="px-3 py-2 rounded-sm border border-white/15 text-white/60 hover:text-white transition-colors"
          >
            <RefreshCw size={15} className={ordersLoading ? "animate-spin" : ""} />
          </button>
        </div>

        {ordersError && (
          <p data-testid="admin-orders-error" className="text-red-400 text-sm mb-4">{ordersError}</p>
        )}

        <div className="rounded-sm border border-white/10 bg-white/[0.03] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/50">Payment ID</TableHead>
                <TableHead className="text-white/50">Product</TableHead>
                <TableHead className="text-white/50">Amount</TableHead>
                <TableHead className="text-white/50">Email</TableHead>
                <TableHead className="text-white/50">Verified Via</TableHead>
                <TableHead className="text-white/50">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && !ordersLoading && (
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableCell colSpan={6} className="text-white/40 text-center py-10">
                    No orders found.
                  </TableCell>
                </TableRow>
              )}
              {orders.map((o) => (
                <TableRow key={o.payment_id} data-testid={`admin-order-row-${o.payment_id}`} className="border-white/10 hover:bg-white/[0.04]">
                  <TableCell className="text-white/80 font-mono text-xs">{o.payment_id}</TableCell>
                  <TableCell className="text-white/80">{o.product_title || o.slug}</TableCell>
                  <TableCell className="text-white/80">{formatAmount(o.amount, o.currency)}</TableCell>
                  <TableCell className="text-white/60">{o.email || "—"}</TableCell>
                  <TableCell className="text-white/40 text-xs">
                    <span className="inline-flex items-center gap-1">
                      <ShieldCheck size={12} className="text-[#F57F17]" /> {o.verified_via || "—"}
                    </span>
                  </TableCell>
                  <TableCell className="text-white/60 text-xs">{formatDate(o.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
